import { useState, useContext, useEffect } from "react";
import { BackNavigation } from "../partials/BackNavigation";
import { Divider } from "../../../../../Resources/Scripts/_Shared/elements/Divider";
import { WaitingListFlowContext } from "../WaitingListFlowContext";
import _ from "lodash";
import {
    base64ToFile,
    fileToBase64,
    hasWaitingListAttachment,
    isWaitingListAttachmentRequired,
} from "../util";
import Instructions from "../partials/Instructions";
import { waitinglist } from "../../../_Shared/Scripts/Endpoints";

const DocumentUpload = () => {
    const { waitingListFlowContext, setWaitingListFlowContext } = useContext(
        WaitingListFlowContext
    );

    const existingDocument = waitingListFlowContext.document;

    const [selectedFile, setSelectedFile] = useState(() => {
        if (existingDocument.selectedFile) {
            const base64String = existingDocument?.selectedFile;
            const file = base64ToFile(
                base64String,
                existingDocument?.fileMeta?.name,
                existingDocument.fileMeta?.mimeType
            );

            if (file && file.name && file.size) {
                return file;
            }

            const meta = existingDocument?.fileMeta;
            if (meta?.name) {
                return { fileName: meta.fileName, sizeBytes: meta.sizeBytes };
            }
        }

        return null;
    });

    const [error, setError] = useState("");

    const attachmentMissing =
        isWaitingListAttachmentRequired(waitingListFlowContext) &&
        !hasWaitingListAttachment(waitingListFlowContext);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        const maxSize = 5 * 1024 * 1024;

        const allowedTypes = [
            "application/pdf",
            "text/plain",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "image/png",
            "image/jpeg",
        ];

        if (!file) {
            return;
        }

        if (!allowedTypes.includes(file.type)) {
            setError(
                "Please upload a valid PDF, TXT, DOC, DOCX, PNG, JPG, or JPEG file."
            );
            e.target.value = null;
            return;
        }

        if (file.size > maxSize) {
            setError("File size must be 5MB or less.");
            e.target.value = null;
            return;
        }

        try {
            const pmcId = waitingListFlowContext.product.Id;
            const prepareResponse = await waitinglist.prepareUpload({ pmcId, mimeType: file.type });

            if (prepareResponse.status !== 200 || !prepareResponse.body?.PreSignedUrl) {
                console.error("Prepare upload failed", prepareResponse);
                setError("Upload failed.");
                e.target.value = null;
                return;
            }

            const { PreSignedUrl: preSignedUrl, AttachmentId: attachmentId } = prepareResponse.body;

            const s3Response = await fetch(preSignedUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type },
            });

            if (!s3Response.ok) {
                console.error("S3 upload failed", s3Response.status);
                setError("Upload failed.");
                e.target.value = null;
                return;
            }

            const attachmentRef = {
                Uid: attachmentId,
                FileName: file.name,
                MimeType: file.type,
                SizeBytes: file.size,
            };

            setSelectedFile(file);
            addFile(file, attachmentRef);
            setError("");
        } catch (err) {
            console.error("Upload failed", err);
            setError("Upload failed.");
        }
    };

    const finalizeFile = () => {
        if (attachmentMissing) {
            return;
        }

        setWaitingListFlowContext({
            ...waitingListFlowContext,
            activeStep: waitingListFlowContext.overview.index,
        });
    };

    const addFile = async (file, metaData) => {
        const base64 = await fileToBase64(file);
        setWaitingListFlowContext({
            ...waitingListFlowContext,
            document: {
                ...waitingListFlowContext.document,
                selectedFile: base64,
                fileMeta: {
                    uid: metaData.Uid,
                    name: metaData.FileName,
                    sizeBytes: metaData.SizeBytes,
                    mimeType: metaData.MimeType,
                },
            },
        });
    };

    const approvedInstructions = [
        waitingListFlowContext.localization.uploadDocument?.instructions
            ?.approved?.step1,
        waitingListFlowContext.localization.uploadDocument?.instructions
            ?.approved?.step2,
        waitingListFlowContext.localization.uploadDocument?.instructions
            ?.approved?.step3,
    ];

    const requiredInstructions = [
        waitingListFlowContext.localization.uploadDocument?.instructions
            ?.required?.step1,
        waitingListFlowContext.localization.uploadDocument?.instructions
            ?.required?.step2,
        waitingListFlowContext.localization.uploadDocument?.instructions
            ?.required?.step3,
    ];

    return (
        <div className="waitinglistflowpage__app waitinglistflowpage__app--unreservedproduct">
            <div className="single-step single-step--booking">
                <h2>
                    {waitingListFlowContext.localization.uploadDocument.title}
                </h2>
                <div className="waitinglistflowpage__add-upload-document-container">
                    <h3 className="left waitinglistflowpage__add-upload-document__title">
                        {
                            waitingListFlowContext.localization.uploadDocument
                                .description
                        }
                    </h3>

                    <Instructions
                        title={
                            waitingListFlowContext.localization.uploadDocument
                                ?.instructions?.approved?.title
                        }
                        instructions={approvedInstructions}
                        titleLeft={true}
                    />

                    <Instructions
                        title={
                            waitingListFlowContext.localization.uploadDocument
                                ?.instructions?.required?.title
                        }
                        instructions={requiredInstructions}
                        titleLeft={true}
                    />

                    <div className="waitinglistflowpage__add-upload-document__file">
                        <input
                            type="file"
                            id="file-upload"
                            accept=".pdf,.txt,.doc,.docx,.png,.jpeg,.jpg"
                            onChange={handleFileChange}
                            className="file-upload-hidden"
                        />
                        <label htmlFor="file-upload" className="upload-button">
                            {
                                waitingListFlowContext.localization
                                    .uploadDocument.upload
                            }
                        </label>

                        {selectedFile && (
                            <p className="margin">
                                <strong>Selected:</strong> {selectedFile.name}
                                {!selectedFile.size && (
                                    <span className="warning">
                                        {" "}
                                        (Please re-upload this file)
                                    </span>
                                )}
                            </p>
                        )}
                    </div>

                    {error && (
                        <p className="waitinglistflowpage__add-upload-document__error">
                            {error}
                        </p>
                    )}

                    {attachmentMissing && (
                        <p className="waitinglistflowpage__add-upload-document__error">
                            {
                                waitingListFlowContext.localization
                                    .uploadDocument.requiredError
                            }
                        </p>
                    )}

                    <Divider />

                    <div className="CTA-buttons">
                        <BackNavigation type="CTA" />
                        <button
                            className="single-step__button single-step__button--next"
                            disabled={attachmentMissing}
                            onClick={finalizeFile}
                        >
                            {
                                waitingListFlowContext.localization.global
                                    .buttons.nextStep
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentUpload;
